import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings as SettingsIcon, Loader2, LogOut, User, Building2, ShieldCheck } from 'lucide-react';
import { Sidebar, Topbar } from './Dashboard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { auth, clearSession, getStoredUser, saveSession } from '../api';
import { FullscreenLoading } from './Time';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { SubscriptionPanel } from '../components/SubscriptionPanel';

export default function SettingsPage() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState(getStoredUser());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', firm: '' });

  useEffect(() => {
    if (!localStorage.getItem('ad_token')) { nav('/login'); return; }
    (async () => {
      try {
        const me = await auth.me();
        setUser(me);
        setForm({ name: me.name || '', firm: me.firm || '' });
      } catch {
        // 401 handled by interceptor
      } finally { setLoading(false); }
    })();
  }, [nav]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const updated = await auth.updateProfile(form);
      setUser(updated);
      // Update stored user (keep existing token)
      const token = localStorage.getItem('ad_token');
      if (token) saveSession({ token, user: updated });
      toast({ title: 'Profile updated', description: 'Your changes have been saved.' });
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const logout = () => { clearSession(); nav('/'); };

  if (loading) return <FullscreenLoading label="Loading settings..."/>;

  const isOwner = user?.firmRole === 'owner';

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar activeLabel="Settings"/>
      <div className="flex-1 min-w-0">
        <Topbar user={user} firm={user?.firm}/>
        <main className="p-4 lg:p-8 space-y-6 max-w-3xl">
          <div>
            <h1 className="text-2xl lg:text-3xl font-serif font-semibold text-stone-900 flex items-center gap-2">
              <SettingsIcon size={24} className="text-emerald-800"/> Settings
            </h1>
            <p className="mt-1 text-stone-600 text-sm">Manage your profile, firm details{isOwner ? ' and subscription' : ''}.</p>
          </div>

          <Tabs defaultValue={new URLSearchParams(window.location.search).get('tab') === 'subscription' && isOwner ? 'subscription' : 'profile'}>
            <TabsList data-testid="settings-tabs">
              <TabsTrigger value="profile" data-testid="settings-tab-profile">Profile</TabsTrigger>
              {isOwner && <TabsTrigger value="subscription" data-testid="settings-tab-subscription">Subscription</TabsTrigger>}
            </TabsList>
            {isOwner && <TabsContent value="subscription" className="mt-6"><SubscriptionPanel user={user}/></TabsContent>}
            <TabsContent value="profile" className="mt-6 space-y-6">
          <form onSubmit={save} className="rounded-xl bg-white border border-stone-200 overflow-hidden">
            <div className="p-5 border-b border-stone-100 flex items-center gap-2">
              <User size={18} className="text-emerald-800"/>
              <div className="font-semibold text-stone-900">Profile</div>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5"/>
              </div>
              <div>
                <Label htmlFor="email">Work email</Label>
                <Input id="email" value={user?.email || ''} disabled className="mt-1.5 bg-stone-50 text-stone-500"/>
                <div className="mt-1 text-xs text-stone-500">Email cannot be changed. Contact support to move to a different address.</div>
              </div>
              <div>
                <Label htmlFor="firm">Firm name</Label>
                <Input id="firm" value={form.firm} onChange={(e) => setForm({ ...form, firm: e.target.value })} disabled={!isOwner} className="mt-1.5"/>
                {!isOwner && <div className="mt-1 text-xs text-stone-500">Only the firm owner can rename the firm.</div>}
              </div>
              <div>
                <Label>Your role</Label>
                <div className="mt-1.5 flex items-center gap-2 text-sm">
                  <ShieldCheck size={14} className="text-emerald-700"/>
                  <span className="font-medium text-stone-900 capitalize">{user?.firmRole || 'member'}</span>
                  <span className="text-stone-500">— {user?.firm}</span>
                </div>
              </div>
            </div>
            <div className="px-5 py-4 bg-stone-50 border-t border-stone-100 flex justify-end">
              <Button type="submit" disabled={busy} className="bg-emerald-800 hover:bg-emerald-900 text-white">
                {busy ? <><Loader2 size={16} className="mr-2 animate-spin"/> Saving...</> : 'Save changes'}
              </Button>
            </div>
          </form>

          <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
            <div className="p-5 border-b border-stone-100 flex items-center gap-2">
              <Building2 size={18} className="text-emerald-800"/>
              <div className="font-semibold text-stone-900">Session</div>
            </div>
            <div className="p-5 flex items-center justify-between gap-4">
              <div className="text-sm text-stone-700">You&apos;re signed in as <strong>{user?.email}</strong>. Signing out clears your session on this device.</div>
              <Button onClick={logout} variant="outline" className="border-red-200 text-red-700 hover:bg-red-50">
                <LogOut size={16} className="mr-2"/> Sign out
              </Button>
            </div>
          </div>
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
}
